import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import directorApi from "@contexts/director/api";
import { useDirectorContext } from "@contexts/director";
import { formatCents, formatCivilDate, formatCostCenterRole } from "../../utils/directorFormat";
import { companyQuery, currentCompetence, shiftCompetence } from "./module1Utils";

const CentersTitles = ({ titles = false }) => {
  const { selectedCompanyId, metadata, filters = {} } = useDirectorContext();
  const [state, setState] = useState({
    data: null,
    dataSignature: null,
    loading: false,
    error: null,
  });
  const period = useMemo(() => {
    const from = shiftCompetence(currentCompetence(), -12);
    return { from, to: shiftCompetence(from, 35), basis: "nominal", page: 1, pageSize: 50 };
  }, []);
  const titleFilters = useMemo(() => ({
    ...period,
    projectKey: filters.projectKey || null,
  }), [filters.projectKey, period]);
  const requestSignature = useMemo(
    () => JSON.stringify(["titles", selectedCompanyId, titleFilters]),
    [selectedCompanyId, titleFilters],
  );
  useEffect(() => {
    let active = true;
    if (!titles || !selectedCompanyId) return () => { active = false; };
    setState({ data: null, dataSignature: requestSignature, loading: true, error: null });
    directorApi.fetchDirectorTitles(selectedCompanyId, titleFilters)
      .then((data) => active && setState({
        data,
        dataSignature: requestSignature,
        loading: false,
        error: null,
      }))
      .catch((error) => active && setState({
        data: null,
        dataSignature: requestSignature,
        loading: false,
        error,
      }));
    return () => { active = false; };
  }, [requestSignature, selectedCompanyId, titleFilters, titles]);
  const centers = (metadata?.costCenters || []).filter((center) => (
    !filters.projectKey || center.projectKey === filters.projectKey
  ));
  const signedState = state.dataSignature === requestSignature
    ? { ...state, requestSignature }
    : {
        data: null,
        dataSignature: null,
        requestSignature,
        loading: Boolean(titles && selectedCompanyId),
        error: null,
      };
  const provenanceMap = metadata?.dataProvenanceByProject;
  const safeProvenanceMap = provenanceMap
    && typeof provenanceMap === "object"
    && !Array.isArray(provenanceMap)
    ? provenanceMap
    : {};
  const authorizedProjectKeys = new Set(
    (metadata?.projects || []).map((project) => project.key),
  );
  const metadataQualityData = filters.projectKey
    ? (authorizedProjectKeys.has(filters.projectKey)
      && Object.prototype.hasOwnProperty.call(safeProvenanceMap, filters.projectKey)
      ? safeProvenanceMap[filters.projectKey]
      : null)
    : [...authorizedProjectKeys]
        .filter((projectKey) => Object.prototype.hasOwnProperty.call(
          safeProvenanceMap,
          projectKey,
        ))
        .map((projectKey) => safeProvenanceMap[projectKey]);
  const metadataSignature = JSON.stringify([
    "metadata-provenance",
    selectedCompanyId,
    metadata?.portfolioId ?? null,
    metadata?.portfolioVersion ?? null,
    filters.projectKey ?? null,
  ]);
  const provenanceSource = {
    data: metadataQualityData,
    dataSignature: metadataSignature,
    requestSignature: metadataSignature,
  };
  const qualitySources = titles
    ? [signedState, provenanceSource]
    : [provenanceSource];
  return <ModuleShell
    title={titles ? "Drill-down de títulos" : "Explorador de centros de custo"}
    description={titles ? "Títulos e rateios permanecem fatos separados e paginados." : "Navegue da classificação financeira aos títulos que compõem cada centro."}
    badge="Escopo do portfólio"
    qualitySources={qualitySources}
  >
    {titles && <ModuleState loading={signedState.loading} error={signedState.error} availability={signedState.data?.state} />}
    {!titles && <section className="director-module1__split">
      <article className="director-module1__panel"><header><div><span className="director-eyebrow">Centros top-level</span><h2>{centers.length} centro(s) no escopo</h2></div></header><div className="director-module1__center-grid">{centers.map((item) => <article key={`${item.legalEntityId}-${item.id}`}><span>Centro {item.id}</span><strong>{item.name || formatCostCenterRole(item.role)}</strong><small>{formatCostCenterRole(item.role)} · entidade {item.legalEntityId}</small></article>)}</div></article>
      <aside className="director-module1__insight"><span className="director-eyebrow">Como navegar</span><h2>Do centro ao título</h2><p>O rateio define a participação do centro; ele não multiplica o valor do título.</p><Link className="director-button" to={`/dashboard/director/titles${companyQuery(selectedCompanyId)}`}>Abrir títulos</Link></aside>
    </section>}
    {titles && signedState.data && <section className="director-module1__split">
      <article className="director-module1__panel"><header><div><span className="director-eyebrow">Títulos financeiros</span><h2>{signedState.data.pagination?.totalItems || 0} resultado(s)</h2></div></header><div className="director-table-scroll"><table className="director-table"><thead><tr><th>Vencimento</th><th>Direção</th><th>Estado</th><th>Valor bruto</th><th>Em aberto</th></tr></thead><tbody>{(signedState.data.items || []).map((item) => <tr key={item.titleKey}><td>{formatCivilDate(item.dueDate)}</td><td>{item.direction === "inflow" ? "Entrada" : "Saída"}</td><td>{item.state}</td><td>{formatCents(item.grossAmountCents)}</td><td>{formatCents(item.openAmountCents)}</td></tr>)}</tbody></table></div></article>
      <aside className="director-module1__insight"><span className="director-eyebrow">Contrato canônico</span><h2>Título, rateio e movimento são diferentes</h2><p>Baixas confirmadas e parcelas abertas usam estados separados. Movimentos bancários ficam como evidência de conciliação e não entram novamente no caixa.</p></aside>
    </section>}
  </ModuleShell>;
};

export default CentersTitles;
